import React, { useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import KeyIcon from '@mui/icons-material/Key'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SettingsIcon from '@mui/icons-material/Settings'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { CustomerService, ICustomer } from '@/api/services/CustomerService'
import { DeveloperService } from '@/api/services/DeveloperService'
import { sectionTitleSx, subtleLabelSx, primaryButtonSx, outlinedButtonSx, cardSx, tableHeadCellSx, compactFormSx, checkboxSx } from '../styles'
import { CopyValue } from '../CopyValue'

const DEFAULT_UPDATE_FORM = { autoGenerate: false, accountNumber: '', bsb: '', riskPercentile: '' }

// Fallback for test users with no password set (email-OTP-only accounts) — opens the webapp's
// own Auth0 login with the email pre-filled, so an admin can complete the code entry themselves.
// This needs the webapp's Auth0 app config (domain/client id/audience/redirect uri/scope), which
// we deliberately do NOT hardcode or commit to this repo. Each admin enters it once and it's kept
// only in their own browser's localStorage — never in source control.
interface IWebappAuth0Config {
  domain: string
  clientId: string
  audience: string
  redirectUri: string
  scope: string
}

const WEBAPP_CONFIG_STORAGE_KEY = 'developer-webapp-auth0-login-config'
const EMPTY_WEBAPP_CONFIG: IWebappAuth0Config = { domain: '', clientId: '', audience: '', redirectUri: '', scope: 'openid profile email' }

function loadWebappConfig(): IWebappAuth0Config | null {
  try {
    const raw = localStorage.getItem(WEBAPP_CONFIG_STORAGE_KEY)
    if (!raw) return null
    // Merge onto defaults so stale/partial data saved by an older version of this dialog
    // (e.g. missing scope) doesn't end up sending the literal string "undefined" to Auth0.
    const merged = { ...EMPTY_WEBAPP_CONFIG, ...JSON.parse(raw) }
    return merged.domain && merged.clientId && merged.redirectUri ? merged : null
  } catch {
    return null
  }
}

// Builds the webapp's own Auth0 /authorize URL with the email pre-filled via login_hint.
function buildWebappLoginUrl(config: IWebappAuth0Config, email: string): string {
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    redirect_uri: config.redirectUri,
    scope: config.scope,
    connection: 'email',
    login_hint: email,
    ...(config.audience && { audience: config.audience }),
  })
  return `https://${config.domain}/authorize?${params.toString()}`
}

// Auto-filled test users are created as `<local-part>+<suffix>@<domain>` of whoever created
// them (see randomEmail in CreateUser/index.tsx) — searching by that local part surfaces
// everything the current user has made, without needing a real "created by" field on the backend.
function emailPrefix(email: string | undefined) {
  return email?.split('@')[0] ?? ''
}

export const MyTestUsers: React.FC = () => {
  const { user } = useAuth0()
  const defaultPrefix = emailPrefix(user?.email)
  const [search, setSearch] = useState(defaultPrefix)
  const [submittedSearch, setSubmittedSearch] = useState(defaultPrefix)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(null)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [updateForm, setUpdateForm] = useState(DEFAULT_UPDATE_FORM)
  const [webappConfig, setWebappConfig] = useState<IWebappAuth0Config | null>(loadWebappConfig)
  const [configOpen, setConfigOpen] = useState(false)
  const [configDraft, setConfigDraft] = useState<IWebappAuth0Config>(webappConfig ?? EMPTY_WEBAPP_CONFIG)

  const { data, isLoading, isError, isFetching } = useQuery({
    queryKey: ['developer-my-test-users', submittedSearch],
    queryFn: () => CustomerService.getAll({ search: submittedSearch, page: 1, pageSize: 50 }),
    enabled: !!submittedSearch,
  })

  const tokenMutation = useMutation({
    mutationFn: DeveloperService.generateAuthToken,
  })

  const updateMutation = useMutation({
    mutationFn: DeveloperService.updateTestUsers,
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmittedSearch(search.trim())
  }

  const handleCopy = async (value: string, id: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 1500)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleSelectCustomer = (c: ICustomer) => {
    // Clicking the already-selected row closes the panel instead of re-opening it
    setSelectedCustomer(prev => (prev?.id === c.id ? null : c))
    setPassword('')
    setShowPassword(false)
    setUpdateForm(DEFAULT_UPDATE_FORM)
    tokenMutation.reset()
    updateMutation.reset()
  }

  const handleGenerateToken = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedCustomer) return
    tokenMutation.mutate({ email: selectedCustomer.email, password })
  }

  const handleOpenWebappLogin = () => {
    if (!selectedCustomer || !webappConfig) return
    window.open(buildWebappLoginUrl(webappConfig, selectedCustomer.email), '_blank', 'noopener,noreferrer')
  }

  const handleOpenConfig = () => {
    setConfigDraft(webappConfig ?? EMPTY_WEBAPP_CONFIG)
    setConfigOpen(true)
  }

  const handleSaveConfig = () => {
    localStorage.setItem(WEBAPP_CONFIG_STORAGE_KEY, JSON.stringify(configDraft))
    setWebappConfig(configDraft)
    setConfigOpen(false)
  }

  const isConfigDraftValid = !!configDraft.domain && !!configDraft.clientId && !!configDraft.redirectUri

  // Bank details are only complete (and worth sending) once both fields are filled, or auto-generate is on
  const hasCompleteBankDetails = updateForm.autoGenerate || (!!updateForm.accountNumber && !!updateForm.bsb)

  const handleUpdateUser = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedCustomer) return
    updateMutation.mutate([{
      rowId: selectedCustomer.id,
      identifierType: 'userId',
      identifierValue: selectedCustomer.id,
      enableBankDetails: hasCompleteBankDetails,
      accountNumber: updateForm.accountNumber,
      bsb: updateForm.bsb,
      autoGenerate: updateForm.autoGenerate,
      enableRiskPercentile: updateForm.riskPercentile !== '',
      riskPercentile: updateForm.riskPercentile,
    }])
  }

  const isUpdateFormValid = hasCompleteBankDetails || updateForm.riskPercentile !== ''

  const customers = data?.data ?? []

  return (
    <Card variant="outlined" sx={{ borderRadius: 3, borderColor: '#e0d7e4' }}>
      <CardContent sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" sx={{ ...sectionTitleSx, mb: 0.5 }}>
          My test users
        </Typography>
        <Typography variant="body2" sx={{ ...subtleLabelSx, mb: 2 }}>
          Searches by the email prefix used when a test user was created — defaults to yours.
        </Typography>

        <Box component="form" onSubmit={handleSearch} sx={{ ...compactFormSx, display: 'flex', gap: 1, mb: 2.5, maxWidth: 480 }}>
          <TextField
            label="Email prefix"
            value={search}
            onChange={e => setSearch(e.target.value)}
            fullWidth size="small"
            placeholder={defaultPrefix || 'e.g. jane.doe'}
          />
          <Button
            type="submit" variant="contained" size="medium"
            disabled={!search.trim() || isFetching}
            startIcon={isFetching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            sx={{ ...primaryButtonSx, whiteSpace: 'nowrap' }}
          >
            Search
          </Button>
        </Box>

        {isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to search test users. Check the API logs.
          </Alert>
        )}

        {!isLoading && !isError && submittedSearch && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Chip
              label={`${customers.length} found`}
              size="small" variant="outlined"
              sx={{ fontWeight: 500, borderColor: '#cac0cf', color: '#4b2b5c' }}
            />
          </Box>
        )}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: '#fa624b' }} />
          </Box>
        ) : !submittedSearch ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1 }}>
            <PersonSearchIcon sx={{ fontSize: 40, color: '#e0d7e4' }} />
            <Typography variant="body2" color="#4b2b5c">Enter an email prefix to search</Typography>
          </Box>
        ) : !isError && customers.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1 }}>
            <PersonSearchIcon sx={{ fontSize: 40, color: '#e0d7e4' }} />
            <Typography variant="body2" color="#4b2b5c">No users found</Typography>
            <Typography variant="caption" color="#a08fa8">Try a shorter or different email prefix</Typography>
          </Box>
        ) : !isError && (
          <TableContainer
            component={Paper} variant="outlined"
            sx={{ ...cardSx, maxHeight: 440, overflow: 'auto' }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Name / Email', 'Mobile', 'Created at', 'ID'].map(h => (
                    <TableCell key={h} sx={tableHeadCellSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {customers.map(c => (
                  <TableRow
                    key={c.id}
                    hover
                    onClick={() => handleSelectCustomer(c)}
                    selected={selectedCustomer?.id === c.id}
                    sx={{
                      cursor: 'pointer',
                      '&.Mui-selected': { backgroundColor: '#f5f2f7' },
                      '&.Mui-selected:hover': { backgroundColor: '#f5f2f7' },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} color="#2c063f">
                        {c.firstName} {c.lastName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="caption" color="#a08fa8">{c.email}</Typography>
                        <Tooltip title={copiedId === `email-${c.id}` ? 'Copied!' : 'Copy email'}>
                          <IconButton
                            size="small"
                            onClick={e => { e.stopPropagation(); handleCopy(c.email, `email-${c.id}`) }}
                            aria-label="Copy email"
                            sx={{ p: 0.3 }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 12, color: '#a08fa8' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#4b2b5c">{c.mobile}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#4b2b5c">
                        {c.createdAt ? new Date(c.createdAt).toLocaleString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ fontFamily: 'monospace', bgcolor: '#f5f2f7', color: '#4b2b5c', px: 0.8, py: 0.2, borderRadius: 1, fontSize: 11 }}
                        >
                          {c.id?.slice(0, 8) ?? '—'}…
                        </Typography>
                        <Tooltip title={copiedId === c.id ? 'Copied!' : 'Copy full ID'}>
                          <IconButton
                            size="small"
                            onClick={e => { e.stopPropagation(); handleCopy(c.id, c.id) }}
                            aria-label="Copy full ID"
                            sx={{ p: 0.3 }}
                          >
                            <ContentCopyIcon sx={{ fontSize: 13, color: '#a08fa8' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {selectedCustomer && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              border: '1px solid #e0d7e4',
              backgroundColor: '#faf9fb',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Typography variant="subtitle2" sx={sectionTitleSx}>Generate auth token</Typography>
                <Typography variant="caption" color="#a08fa8">for {selectedCustomer.email}</Typography>
              </Box>
              <IconButton size="small" onClick={() => setSelectedCustomer(null)} aria-label="Close panel" sx={{ p: 0.3 }}>
                <CloseIcon sx={{ fontSize: 16, color: '#a08fa8' }} />
              </IconButton>
            </Box>

            <Box component="form" onSubmit={handleGenerateToken} sx={{ ...compactFormSx, display: 'flex', gap: 1, maxWidth: 480 }}>
              <TextField
                label="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                fullWidth size="small" required
                type={showPassword ? 'text' : 'password'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowPassword(prev => !prev)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit" variant="contained" size="medium"
                disabled={!password || tokenMutation.isPending}
                startIcon={tokenMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <KeyIcon />}
                sx={{ ...primaryButtonSx, whiteSpace: 'nowrap' }}
              >
                Generate
              </Button>
            </Box>

            {tokenMutation.isError && (
              <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
                Failed to generate token. Check the password and try again.
              </Alert>
            )}

            {tokenMutation.isError && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="#a08fa8">
                  If this account has no password (an email-code-only user), try logging in via the webapp instead.
                </Typography>
                {webappConfig ? (
                  <>
                    <Button
                      size="small" variant="outlined"
                      onClick={handleOpenWebappLogin}
                      startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      sx={{ ...outlinedButtonSx, whiteSpace: 'nowrap' }}
                    >
                      Log in via webapp
                    </Button>
                    <Tooltip title="Reconfigure webapp login">
                      <IconButton size="small" onClick={handleOpenConfig} aria-label="Reconfigure webapp login" sx={{ p: 0.3 }}>
                        <SettingsIcon sx={{ fontSize: 14, color: '#a08fa8' }} />
                      </IconButton>
                    </Tooltip>
                  </>
                ) : (
                  <Button
                    size="small" variant="outlined"
                    onClick={handleOpenConfig}
                    startIcon={<SettingsIcon sx={{ fontSize: 14 }} />}
                    sx={{ ...outlinedButtonSx, whiteSpace: 'nowrap' }}
                  >
                    Configure webapp login
                  </Button>
                )}
              </Box>
            )}

            {tokenMutation.isSuccess && (
              <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, border: '1px solid #e0d7e4', backgroundColor: '#f5f2f7', wordBreak: 'break-all' }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.5, color: '#a08fa8' }}>
                  Token
                </Typography>
                <CopyValue value={tokenMutation.data} />
              </Box>
            )}

            <Divider sx={{ my: 2, borderColor: '#e0d7e4' }} />

            <Typography variant="subtitle2" sx={{ ...sectionTitleSx, mb: 0.5 }}>Update user</Typography>
            <Typography variant="caption" sx={{ ...subtleLabelSx, display: 'block', mb: 1 }}>
              Leave bank or risk fields empty to skip them
            </Typography>

            <Box component="form" onSubmit={handleUpdateUser} sx={compactFormSx}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={updateForm.autoGenerate}
                      onChange={e => setUpdateForm(prev => ({
                        ...prev,
                        autoGenerate: e.target.checked,
                        accountNumber: e.target.checked ? '' : prev.accountNumber,
                        bsb: e.target.checked ? '' : prev.bsb,
                      }))}
                      sx={checkboxSx}
                    />
                  }
                  label={<Typography variant="body2" color="#4b2b5c">Auto-generate bank details</Typography>}
                />
                <TextField
                  label="Account number"
                  value={updateForm.accountNumber}
                  onChange={e => setUpdateForm(prev => ({ ...prev, accountNumber: e.target.value }))}
                  size="small" disabled={updateForm.autoGenerate}
                  sx={{ width: 180 }}
                />
                <TextField
                  label="BSB"
                  value={updateForm.bsb}
                  onChange={e => setUpdateForm(prev => ({ ...prev, bsb: e.target.value }))}
                  size="small" disabled={updateForm.autoGenerate}
                  sx={{ width: 110 }}
                />
                <TextField
                  label="Risk %"
                  value={updateForm.riskPercentile}
                  onChange={e => setUpdateForm(prev => ({ ...prev, riskPercentile: e.target.value }))}
                  size="small" type="number"
                  inputProps={{ min: 0, max: 100, step: 1 }}
                  sx={{ width: 90 }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit" variant="contained" size="medium"
                  disabled={!isUpdateFormValid || updateMutation.isPending}
                  startIcon={updateMutation.isPending ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
                  sx={primaryButtonSx}
                >
                  Update user
                </Button>
              </Box>
            </Box>

            {updateMutation.isError && (
              <Alert severity="error" sx={{ mt: 1.5, py: 0.5 }}>
                Failed to update user. Check the API logs.
              </Alert>
            )}

            {updateMutation.isSuccess && (
              <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }}>
                User updated successfully.
              </Alert>
            )}
          </Box>
        )}
      </CardContent>

      <Dialog open={configOpen} onClose={() => setConfigOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={sectionTitleSx}>Configure webapp login</DialogTitle>
        <DialogContent sx={compactFormSx}>
          <Typography variant="caption" sx={{ ...subtleLabelSx, display: 'block', mb: 2 }}>
            The webapp's Auth0 app details, used only to build a login link for email-code-only
            test users. Saved locally in your browser only — never sent anywhere or committed to source.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField
              label="Auth0 domain" size="small" fullWidth
              placeholder="your-tenant.auth0.com"
              value={configDraft.domain}
              onChange={e => setConfigDraft(prev => ({ ...prev, domain: e.target.value.trim() }))}
            />
            <TextField
              label="Client ID" size="small" fullWidth
              value={configDraft.clientId}
              onChange={e => setConfigDraft(prev => ({ ...prev, clientId: e.target.value.trim() }))}
            />
            <TextField
              label="Redirect URI" size="small" fullWidth
              placeholder="https://your-webapp-domain/callback"
              value={configDraft.redirectUri}
              onChange={e => setConfigDraft(prev => ({ ...prev, redirectUri: e.target.value.trim() }))}
            />
            <TextField
              label="Audience (optional)" size="small" fullWidth
              value={configDraft.audience}
              onChange={e => setConfigDraft(prev => ({ ...prev, audience: e.target.value.trim() }))}
            />
            <TextField
              label="Scope" size="small" fullWidth
              value={configDraft.scope}
              onChange={e => setConfigDraft(prev => ({ ...prev, scope: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfigOpen(false)} sx={outlinedButtonSx} variant="outlined" size="small">
            Cancel
          </Button>
          <Button onClick={handleSaveConfig} disabled={!isConfigDraftValid} sx={primaryButtonSx} variant="contained" size="small">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
